// The tools on the raisedbeef.com homepage.
//
// ⚠️ THIS FILE IS NO LONGER THE SOURCE OF TRUTH. It is the SEED and the OFFLINE
// FALLBACK.
//
// The live lineup — order, category, free/Terminal, and which tools are
// featured on a given day — lives in the `rb_tools` / `rb_tool_schedule` tables
// and is edited at /manage/tools. This array is what seeds those tables, and
// what the homepage renders when the database cannot be reached. It is
// deliberately never deleted: on a prerendered front door, being able to draw
// the right page with no network is worth keeping permanently.
//
// Practical consequence: editing a name here changes what a NEW deployment
// seeds and what an offline visitor sees. It does not change the live site.
// Change the live site at /manage/tools.
//
// ⚠️ THE PATH IS /manage/tools. `/admin/tools` is NOT a route and never was —
// it was written in several comments here and in tierAccess, and repeated into
// the session docs on 2026-09-13, sending the owner to a page that does not
// exist. Corrected everywhere the same day.
//
// Names are fixed by the design spec and must not be renamed.

export type Weekday =
  | "Monday"
  | "Tuesday"
  | "Wednesday"
  | "Thursday"
  | "Friday"
  | "Saturday"
  | "Sunday";

/**
 * Groups are for SCANNING, not for hiding. Every tool renders on load; the
 * headings just cut two dozen things to look at down to 5. Nothing here is ever
 * a filter, a tab or an accordion — the whole premise of the page is that a
 * rancher sees every tool without clicking.
 */
export const RB_CATEGORIES = [
  /** `live` puts a pulsing indicator beside the heading. Reserved for columns
   *  whose data actually moves during a sale — claiming "live" over a monthly
   *  USDA report would be a lie the user can check. */
  { name: "Live Feeder Market", live: true },
  { name: "Futures & Funds" },
  { name: "Packer & Beef" },
  { name: "Supply & Conditions" },
  { name: "Market Reporting" },
] as const;

export type RbCategory = (typeof RB_CATEGORIES)[number]["name"];

/**
 * Whether a tool costs anything ON RAISEDBEEF.COM.
 *
 * `free`     — opens and works with no account.
 * `terminal` — bounces to sign-in; it lives in RaisedBeef Terminal.
 *
 * ⚠️ This is a DISPLAY LABEL, never an entitlement. Nothing reads it to decide
 * access; the router already does that. It exists so a tile can warn a visitor
 * before they click, instead of every tile looking identical and eight of them
 * dead-ending at a login wall.
 */
export type RbAccess = "free" | "terminal";

export interface RbTool {
  /** Stable identity. Never changes, even when `name` does — it is what the
   *  database row, the audit trail and this seed agree on. */
  slug: string;
  /** Exact display name from the design spec. Do not rename. */
  name: string;
  category: RbCategory;
  /** A key of RB_ICONS. Unknown names degrade to a neutral glyph, never throw
   *  — see rbIcons.ts. */
  iconName: string;
  /** Destination. A `/TODO-` prefix marks a tool with no route yet — see
   *  `isPlaceholder` below, which is what the UI checks. */
  href: string;
  /** One line, shown on the admin page so a curator knows what they are
   *  scheduling. Not rendered on the grid — the grid is names only. */
  shortDescription: string;
  access: RbAccess;
  /**
   * ⚠️ REMOVED, not merely unused. Featuring now lives in `rb_tool_schedule`
   * and is answered by `rpc_rb_tools` for the visitor's own day.
   *
   * A second implementation here is how the two would come to disagree — and
   * this field is the cautionary tale: it sat declared and set by nothing for
   * months while the homepage rendered "Monday's Top Tools", promising a
   * curation that never happened. Schedule tools at /manage/tools.
   */
}

/** True when a tool has no destination yet. These render disabled rather than
 *  navigating somewhere wrong — a dead link on the front door reads as a
 *  broken product, and a link to the wrong tool reads worse. */
export function isPlaceholder(tool: RbTool): boolean {
  return tool.href.startsWith("/TODO-");
}

/**
 * ⚠️ `access` was resolved against the LIVE ROUTER, not guessed from the href.
 *
 * It is host-dependent and the surprises are real. `src/App.tsx:435-437` mounts
 * `/boxed-beef`, `/cattle-on-feed` and `/fed-cattle` OUTSIDE Layout when
 * `isRaisedBeefHomeHost()`, so those three are FREE on raisedbeef.com and
 * tier-gated on every other host — including "Boxed Beef Pro" and "5-Area Pro",
 * which carry "Pro" in a name given before the split existed. `/clara` and
 * `/clara/t/:slug` are also outside the gate; Clara upsells inside the page
 * rather than blocking entry, so the tile opens something that works.
 *
 * Tagging a free page "terminal" is worse than no tag at all, so re-derive from
 * the router if these ever move — do not pattern-match paths.
 *
 * The unbuilt placeholders (Futures, August 5-Wts, Beef Imports, Cattle
 * Weather, Crop Progress, Santa Teresa — all `/TODO-` hrefs) were removed on
 * 2026-09-15 at the owner's request (migration 20260915000001); they opened to
 * nowhere. `isPlaceholder` stays for any future `/TODO-` row.
 */
export const RB_TOOLS: RbTool[] = [
  // What is moving in the ring right now, and who is on top.
  // The PUBLIC tracker, not the tier-gated terminal page at
  // /tools/feeders/national-steer-tracker. Same numbers, no account needed.
  { slug: "steer-tracker", name: "Steer Tracker", category: "Live Feeder Market", iconName: "ChartColumn", href: "/steer-tracker", access: "free",
    shortDescription: "Live 700–899 lb steer prices nationally, updated as lots sell." },
  { slug: "leaderboard", name: "Leaderboard", category: "Live Feeder Market", iconName: "Trophy", href: "/leaderboard", access: "free",
    shortDescription: "Top-priced pens by weight class across every reporting barn." },
  // Owner, 2026-10-10: "we never added Toppers to the app" — Tools tab, beside
  // the Leaderboard. The app opens its own screen (/m/toppers, inApp.tsx).
  { slug: "toppers", name: "Market Toppers", category: "Live Feeder Market", iconName: "Trophy", href: "/toppers", access: "free",
    shortDescription: "The top-priced lot in every weight class today, from every reporting barn." },
  { slug: "joplin-ticker", name: "Joplin Ticker", category: "Live Feeder Market", iconName: "LayoutList", href: "/barn/mo-joplin-regional-stockyards/ticker", access: "free",
    shortDescription: "Joplin Regional's latest lots by weight class on sale day." },
  { slug: "joplin-live", name: "Joplin Live", category: "Live Feeder Market", iconName: "PlayCircle", href: "/barn/mo-joplin-regional-stockyards/live", access: "free",
    shortDescription: "Joplin's full sale report — top lots, receipts, weighted averages." },
  { slug: "missouri-tracker", name: "Missouri Ticker", category: "Live Feeder Market", iconName: "Map", href: "/ticker/missouri", access: "free",
    shortDescription: "Missouri feeder prices by weight class, barn by barn." },
  { slug: "nebraska-calf-tracker", name: "Nebraska Calf Tracker", category: "Live Feeder Market", iconName: "MapPin", href: "/quick-view/state/NE", access: "terminal",
    shortDescription: "Nebraska calf and feeder prices with barn-level detail." },
  // The Weight Maps — one tool per 100 lb class (weight-map/weightMaps.ts), so
  // each can be featured, scheduled or switched off on its own at /manage/tools
  // (owner, 2026-09-13). 400–1000 only: the classes with the numbers.
  { slug: "4-wt-map", name: "4 Wt Map", category: "Live Feeder Market", iconName: "MapPin", href: "/4-wt-map", access: "free",
    shortDescription: "400–499 lb steer and heifer prices by state, today and the past week." },
  { slug: "5-wt-map", name: "5 Wt Map", category: "Live Feeder Market", iconName: "MapPin", href: "/5-wt-map", access: "free",
    shortDescription: "500–599 lb steer and heifer prices by state, today and the past week." },
  { slug: "6-wt-map", name: "6 Wt Map", category: "Live Feeder Market", iconName: "MapPin", href: "/6-wt-map", access: "free",
    shortDescription: "600–699 lb steer and heifer prices by state, today and the past week." },
  { slug: "7-wt-map", name: "7 Wt Map", category: "Live Feeder Market", iconName: "MapPin", href: "/7-wt-map", access: "free",
    shortDescription: "700–799 lb steer and heifer prices by state, today and the past week." },
  { slug: "8-wt-map", name: "8 Wt Map", category: "Live Feeder Market", iconName: "MapPin", href: "/8-wt-map", access: "free",
    shortDescription: "800–899 lb steer and heifer prices by state, today and the past week." },
  { slug: "9-wt-map", name: "9 Wt Map", category: "Live Feeder Market", iconName: "MapPin", href: "/9-wt-map", access: "free",
    shortDescription: "900–999 lb steer and heifer prices by state, today and the past week." },
  { slug: "10-wt-map", name: "10 Wt Map", category: "Live Feeder Market", iconName: "MapPin", href: "/10-wt-map", access: "free",
    shortDescription: "1000–1099 lb steer and heifer prices by state, today and the past week." },
  // Diesel, the other half of a haul's cost. Fuel only — no freight math here,
  // owner's call 2026-09-24. Filed under Supply & Conditions, not Live Feeder
  // Market: it is an input cost, not a cattle price, and should not sit in a
  // row of them. (There is no Fuel category and adding one for a single tool
  // would widen RbCategory for no one else.)
  { slug: "diesel", name: "Diesel", category: "Supply & Conditions", iconName: "Fuel", href: "/diesel", access: "free",
    shortDescription: "Weekly retail diesel from the EIA, national and by region." },
  { slug: "receipts", name: "Steer & Heifer Receipts", category: "Supply & Conditions", iconName: "ChartColumn", href: "/receipts", access: "free",
    shortDescription: "Steer and heifer head through the ring by state and barn. Cows and bulls are not counted." },

  // The paper market.
  { slug: "funds-position", name: "Funds Position", category: "Futures & Funds", iconName: "ChartColumn", href: "/tools/cot-positioning", access: "terminal",
    shortDescription: "CFTC managed-money positioning in cattle futures, weekly." },
  { slug: "corn-chart", name: "Corn Chart", category: "Futures & Funds", iconName: "Sprout", href: "/tools/usda-corn-outlook", access: "terminal",
    shortDescription: "Corn price and USDA balance sheet — the cost side of the gain." },

  // Downstream of the feedyard — what the packer pays and what beef brings.
  { slug: "boxed-beef-pro", name: "Boxed Beef Pro", category: "Packer & Beef", iconName: "Beef", href: "/boxed-beef", access: "free",
    shortDescription: "Cutout AM and PM, seven primals, the Choice/Select gap and the cutout's seasonal shape." },
  // "Fed Cattle", not "5-Area Pro" (owner, 2026-10-12: "name it Fed Cattle"):
  // the page now carries the barns as well as USDA's 5-Area.
  { slug: "5-area-pro", name: "Fed Cattle", category: "Packer & Beef", iconName: "Map", href: "/fed-cattle", access: "free",
    shortDescription: "What packers paid and what the barns brought: USDA 5-Area cash and every barn's fed cattle, live." },
  // Public page since 2026-09-13 (was /market-pulse, choice-gated).
  { slug: "packer-margin", name: "Packer Margin", category: "Packer & Beef", iconName: "DollarSign", href: "/packer-margin", access: "free",
    shortDescription: "Daily packer net margin — 90-day trend and day-by-day changes." },
  // The fat cattle tools (2026-09-16, migration 20260916000004). `access` says
  // WHICH SITE, not what it costs: these live on raisedbeef.com behind the
  // account gate, not on the Terminal.
  { slug: "fat-cattle-sales", name: "Fat Cattle Sales", category: "Packer & Beef", iconName: "Beef", href: "/fat-cattle", access: "free",
    shortDescription: "Every barn with a weekly fat sale, the day it sells, and what its fed cattle brought." },
  { slug: "meat-production", name: "Meat Production", category: "Packer & Beef", iconName: "Factory", href: "/slaughter", access: "terminal",
    shortDescription: "Head slaughtered and beef produced, daily and weekly." },

  // How many cattle there are, and what the weather and the border are doing
  // to them.
  { slug: "cattle-on-feed", name: "Cattle on Feed", category: "Supply & Conditions", iconName: "Warehouse", href: "/cattle-on-feed", access: "free",
    shortDescription: "USDA on-feed, placements and marketings, with revisions." },
  // The Border Board (2026-10-27, migration 20261027000004): USDA AMS 3486,
  // Mexican feeder cattle crossing into the Plains, daily.
  { slug: "border", name: "The Border", category: "Supply & Conditions", iconName: "Globe", href: "/border", access: "free",
    shortDescription: "Mexican feeder cattle crossing today, by port, against a normal week — and what they cost at the port." },

  // Things you read or watch rather than query.
  { slug: "cattle-report", name: "Cattle Report", category: "Market Reporting", iconName: "ClipboardList", href: "/market-report", access: "terminal",
    shortDescription: "The written market report — what moved and what it means." },
  // Lives as a Clara topic rather than a page of its own — see claraTopics.ts.
  { slug: "feeder-flash", name: "Feeder Flash", category: "Market Reporting", iconName: "Zap", href: "/clara/t/feeder-flash", access: "free",
    shortDescription: "Short daily read on the feeder market, from Clara." },
  { slug: "news-network", name: "News Network", category: "Market Reporting", iconName: "Newspaper", href: "/news/briefs", access: "terminal",
    shortDescription: "Cattle and beef headlines, summarised and scored for relevance." },
  { slug: "clara-live", name: "Clara Live", category: "Market Reporting", iconName: "Sparkles", href: "/clara", access: "free",
    shortDescription: "Ask the cattle consultant a question in plain language." },
  // Added 2026-09-15 (migration 20260915000001): live pages that were never registered.
  { slug: "sale-reports", name: "Sale Reports", category: "Market Reporting", iconName: "ClipboardList", href: "/reports", access: "free",
    shortDescription: "Every barn's sale report, with today's final sales ready to print." },
  { slug: "barn-directory", name: "Barn Directory", category: "Live Feeder Market", iconName: "LayoutList", href: "/barns", access: "free",
    shortDescription: "Every reporting sale barn, with a LIVE badge on barns selling now." },
  { slug: "state-directory", name: "State Directory", category: "Live Feeder Market", iconName: "Map", href: "/states", access: "free",
    shortDescription: "Every state with reporting barns, each opening its own ticker." },
  { slug: "lobby-cattle-ticker", name: "Lobby Cattle Ticker", category: "Live Feeder Market", iconName: "ChartColumn", href: "/lobby", access: "free",
    shortDescription: "The touch screen for a lobby TV (cattleticker.raisedbeef.com)." },
];
